import {
  InterfaceBackup,
  InterfaceBackupLog,
  InterfaceBackupLogType,
  InterfaceBackupStatus,
} from '@/types/interface'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)

const BACKUP_DATE_FORMAT = 'DD/MM/YYYY HH:mm:ss'

const byLogTimeDesc = (a: InterfaceBackupLog, b: InterfaceBackupLog) =>
  new Date(b.logTime).getTime() - new Date(a.logTime).getTime()

const getLatestLogOfType = (
  logs: InterfaceBackupLog[] | undefined,
  type: InterfaceBackupLogType,
): InterfaceBackupLog | undefined =>
  (logs ?? []).filter((log) => log.type === type).sort(byLogTimeDesc)[0]

export const getLatestBackupLog = (backup: InterfaceBackup) =>
  getLatestLogOfType(backup.logs, InterfaceBackupLogType.Backup)

export const getLatestRestoreLog = (backup: InterfaceBackup) =>
  getLatestLogOfType(backup.logs, InterfaceBackupLogType.Restore)

/**
 * Backend writes restore results into `latestStatus` too, so a failed restore
 * would make a good backup look failed. Derive the backup's own status from
 * backup-type logs only.
 */
export const getBackupStatus = (
  backup: InterfaceBackup,
): InterfaceBackupStatus => {
  const backupLog = getLatestBackupLog(backup)
  if (backupLog) return backupLog.status
  // Only restores are logged: restore is only offered on completed backups,
  // so the backup itself must have completed.
  return getLatestRestoreLog(backup)
    ? InterfaceBackupStatus.Completed
    : backup.latestStatus
}

/** True while a backup or restore job is still running for this row. */
export const isBackupJobRunning = (backup: InterfaceBackup) =>
  backup.latestStatus === InterfaceBackupStatus.InProgress

const isValidIanaTimeZone = (timeZone?: string) => {
  if (!timeZone || !timeZone.includes('/')) return false
  try {
    Intl.DateTimeFormat(undefined, { timeZone })
    return true
  } catch {
    return false
  }
}

/** Parses "GMT+08:00", "GMT-4", "GMT+5:30" into minutes. */
const parseGmtOffsetMinutes = (timeZone?: string): number | null => {
  const match = timeZone?.match(/GMT\s*([+-])(\d{1,2})(?::?(\d{2}))?/i)
  if (!match) return null
  const sign = match[1] === '-' ? -1 : 1
  return sign * (Number(match[2]) * 60 + Number(match[3] ?? 0))
}

export interface BackupTimeZone {
  ianaId?: string
  gmt?: string
}

/**
 * Backend timestamps are UTC but serialized without a zone suffix. Convert
 * them to the interface's time zone (IANA id first, then GMT offset), falling
 * back to the browser's zone.
 */
export const toInterfaceTime = (
  value: string | undefined,
  timeZone?: BackupTimeZone,
): dayjs.Dayjs | null => {
  if (!value) return null
  const date = dayjs.utc(value)
  if (!date.isValid()) return null

  if (isValidIanaTimeZone(timeZone?.ianaId)) {
    return date.tz(timeZone?.ianaId)
  }
  const offset = parseGmtOffsetMinutes(timeZone?.gmt)
  if (offset !== null) return date.utcOffset(offset)
  return date.local()
}

export const formatBackupDateTime = (
  value: string | undefined,
  timeZone?: BackupTimeZone,
  format = BACKUP_DATE_FORMAT,
): string => toInterfaceTime(value, timeZone)?.format(format) ?? '-'

/** "YYYY-MM-DD" in the interface's zone, used to group backups by day. */
export const getBackupDayKey = (
  value: string | undefined,
  timeZone?: BackupTimeZone,
) => toInterfaceTime(value, timeZone)?.format('YYYY-MM-DD') ?? ''

/** Today's day key in the interface's zone. */
export const getTodayKey = (timeZone?: BackupTimeZone) =>
  getBackupDayKey(dayjs.utc().format('YYYY-MM-DDTHH:mm:ss'), timeZone)

export interface BackupJobActivity {
  backup: InterfaceBackup
  log: InterfaceBackupLog
}

/** The job currently running for this interface, if any. */
export const getRunningJob = (
  backups: InterfaceBackup[] = [],
): BackupJobActivity | undefined => {
  for (const backup of backups) {
    if (!isBackupJobRunning(backup)) continue
    const log = [...(backup.logs ?? [])]
      .filter((item) => item.status === InterfaceBackupStatus.InProgress)
      .sort(byLogTimeDesc)[0]
    if (log) return { backup, log }
    // Row created but the job has not logged yet: it is a backup.
    return {
      backup,
      log: {
        status: InterfaceBackupStatus.InProgress,
        type: InterfaceBackupLogType.Backup,
        logTime: backup.createdAt,
        message: '',
        user: null,
      },
    }
  }
  return undefined
}

/** Newest restore log across the given backups. */
export const getLatestRestoreActivity = (
  backups: InterfaceBackup[] = [],
): BackupJobActivity | undefined =>
  backups
    .map((backup) => {
      const log = getLatestRestoreLog(backup)
      return log ? { backup, log } : undefined
    })
    .filter((item): item is BackupJobActivity => !!item)
    .sort((a, b) => byLogTimeDesc(a.log, b.log))[0]

/** "m:ss" or "h:mm:ss" between a backend UTC timestamp and now. */
export const formatElapsed = (since: string | undefined, now: number) => {
  if (!since) return ''
  const start = dayjs.utc(since).valueOf()
  const total = Math.max(0, Math.floor((now - start) / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = String(total % 60).padStart(2, '0')
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`
}
